import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BulkPasteCard.scss';

const INVALID_STATUSES = ['invalid', 'bare_domain', 'short_link'];
const WARNING_STATUSES = ['conflict', 'out_of_scope', 'not_found', 'no_variant_record', 'already_linked'];

const CHIP_LABELS = {
  variant: 'variant',
  pin: 'pin'
};

const ROW_MESSAGES = {
  invalid: 'Not a valid URL',
  bare_domain: 'Bare domain',
  short_link: 'Shortened URL — expand it and paste the destination',
  not_found: 'Not in database',
  conflict: 'Matched to a different product — will be skipped',
  out_of_scope: 'Matched outside the selected scope — will be skipped',
  no_variant_record: 'No variant record — will be skipped',
  already_linked: 'Already linked'
};

/**
 * Paste textarea + validation ledger. The textarea stays a native input for paste-heavy power
 * users; per-line validation renders as ledger rows beneath it (exceptions only on Unlink,
 * every line with its classification chip on Link).
 */
const BulkPasteCard = props => {
  const { mode, heading, links, onChange, disabled, disabledHelper, previewRows, summary, isPreviewing, maxLinks } = props;

  const lineCount = links.split('\n').filter(line => line.trim()).length;
  const overLimit = lineCount > maxLinks;

  const rows = previewRows || [];
  const invalidRows = rows.filter(row => INVALID_STATUSES.includes(row.status));
  const warningRows = rows.filter(row => WARNING_STATUSES.includes(row.status));
  const hasBareDomain = rows.some(row => row.status === 'bare_domain');
  const ledgerRows = mode === 'link' ? rows : [...invalidRows, ...warningRows];

  // Match loosely (protocol/www/trailing-slash-insensitive): the server dedupes on the
  // normalized stem and echoes only the first raw copy, so an exact match would leave
  // the other spellings of the same URL behind.
  const looseUrlKey = line =>
    line
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');

  const removeLine = link => {
    const target = looseUrlKey(link);
    const remaining = links.split('\n').filter(line => looseUrlKey(line) !== target);
    onChange(remaining.join('\n'));
  };

  const rowChip = row => {
    if (INVALID_STATUSES.includes(row.status)) return <span className='ct-chip error'>{row.status === 'short_link' ? 'short link' : 'invalid'}</span>;
    if (WARNING_STATUSES.includes(row.status)) return <span className='ct-chip warning'>{row.status.replace(/_/g, ' ')}</span>;
    if (mode === 'link' && row.classification) {
      return <span className={cn('ct-chip', row.classification)}>{CHIP_LABELS[row.classification]}</span>;
    }
    return null;
  };

  return (
    <div className='catalog-paste-card'>
      <h2>{heading}</h2>
      {disabled && disabledHelper && <div className='paste-disabled-helper'>{disabledHelper}</div>}

      <textarea
        value={links}
        onChange={e => onChange(e.target.value)}
        placeholder={'https://example.com/product-1\nhttps://example.com/product-2\nhttps://example.com/product-3'}
        rows={12}
        disabled={disabled}
      />
      <div className={cn('link-count', { 'over-limit': overLimit })}>
        {lineCount.toLocaleString()} / {maxLinks.toLocaleString()} links
      </div>

      {overLimit && (
        <div className='paste-banner error'>
          {lineCount.toLocaleString()} links pasted — limit is {maxLinks.toLocaleString()}. Split into two runs.
        </div>
      )}

      {!overLimit && hasBareDomain && (
        <div className='paste-banner error'>
          Bare domains aren't allowed. Paste exact URLs{mode === 'unlink' ? ', or use domain removal scoped to this brand' : ''}.
        </div>
      )}

      {isPreviewing && lineCount > 0 && <div className='paste-checking'>Checking {lineCount.toLocaleString()} links…</div>}

      {!isPreviewing && summary && (
        <div className='paste-summary'>
          {summary.valid.toLocaleString()} valid · {summary.invalid.toLocaleString()} invalid
          {summary.duplicatesRemoved > 0 ? ` · ${summary.duplicatesRemoved.toLocaleString()} duplicates removed` : ''}
        </div>
      )}

      {!isPreviewing && ledgerRows.length > 0 && (
        <ul className='paste-ledger'>
          {ledgerRows.map((row, index) => (
            <li key={`${row.link}-${index}`} className={cn({ invalid: INVALID_STATUSES.includes(row.status), warning: WARNING_STATUSES.includes(row.status) })}>
              <span className='row-link'>{row.link}</span>
              {row.unmatchedPinCount > 0 && mode === 'link' && row.classification === 'variant' && row.status === 'ok' && (
                <span className='row-note'>+{row.unmatchedPinCount} pins will auto-link</span>
              )}
              {ROW_MESSAGES[row.status] && <span className='row-message'>{ROW_MESSAGES[row.status]}</span>}
              {rowChip(row)}
              {INVALID_STATUSES.includes(row.status) && (
                <button type='button' className='row-remove' onClick={() => removeLine(row.link)}>
                  Remove line
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {mode === 'link' && <div className='paste-footnote'>Variant vs pin auto-detected via existing scraper logic.</div>}
    </div>
  );
};

BulkPasteCard.propTypes = {
  mode: PropTypes.oneOf(['unlink', 'link']).isRequired,
  heading: PropTypes.string.isRequired,
  links: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disabledHelper: PropTypes.string,
  previewRows: PropTypes.array,
  summary: PropTypes.object,
  isPreviewing: PropTypes.bool,
  maxLinks: PropTypes.number.isRequired
};

export default BulkPasteCard;
